import React, { useMemo } from "react";
import { filter, get, includes } from "lodash";
import { canCreateChild } from "src/util/customerConfig";
import {
  useGroupTypes,
  useWorkspace,
} from "src/contexts/global/WorkspaceContext";
import { useAuth } from "src/contexts/global/GlobalContext";
import { hasAdminRole } from "src/services/auth";
import MoveGroupEnhancedModal from "src/components/MoveGroupEnhancedModal";
import CloneHierarchyModal from "src/components/CloneHierarchyModal";
import MoveGroupModal from "src/components/MoveGroupModal";
import { GLOBAL_ACTION_KEYS } from "src/components/ConfiguredActionMenu";
import useUpdateTeamDetails from "src/hooks/useUpdateTeamDetails";
import ImagePicker from "src/components/ImagePicker";
import HideTeamModal from "src/components/HideTeamModal";
import AddNewTeamModal from "../Modals/AddNewTeamModal";
import TeamDescriptionEditModal from "../Modals/TeamDescriptionEditModal";
import useHideTeam from "./useHideTeam";

const TEAM_ACTION_MENU_TYPE = {
  CHANGE_TEAM_DETAILS: "change_team_details",
  ADD_TEAM: "add_team",
  DOWNLOAD: "download",
  MOVE_TEAM: "move_team",
  ADVANCED_MOVE: "advanced_move",
  REMOVE_TEAM: "remove_team",
  CLONE_HIERARCHY: "clone_hierarchy",
  SELECT_COVER_IMAGE: "select_cover_image",
};

const defaultActionMenuTypes = [
  TEAM_ACTION_MENU_TYPE.CHANGE_TEAM_DETAILS,
  TEAM_ACTION_MENU_TYPE.ADD_TEAM,
  TEAM_ACTION_MENU_TYPE.DOWNLOAD,
  TEAM_ACTION_MENU_TYPE.MOVE_TEAM,
  TEAM_ACTION_MENU_TYPE.ADVANCED_MOVE,
  TEAM_ACTION_MENU_TYPE.REMOVE_TEAM,
  TEAM_ACTION_MENU_TYPE.CLONE_HIERARCHY,
  TEAM_ACTION_MENU_TYPE.SELECT_COVER_IMAGE,
];

const useTeamActions = ({
  team,
  actionMenuTypes = defaultActionMenuTypes,
  showChangesEnabled,
  onChangeDetailsClick,
  runGroupCacheUpdateOnAddition,
}) => {
  const canHide = useHideTeam(team);
  const workspace = useWorkspace();
  const auth = useAuth();
  const { handleUpdateTeamDetails, canUpdateGroupAttributes } =
    useUpdateTeamDetails({ team });

  const groupTypes = useGroupTypes();

  const actions = useMemo(() => {
    const config = get(groupTypes, team.type, {});
    const moveGroupEnabled = get(
      workspace,
      "config.allocation.enableMoveGroups",
      false
    );
    const moveGroupEnhancedEnabledFlag = get(
      workspace,
      "config.featureFlags.enableEnhancedMove",
      false
    );
    const moveGroupEnhancedEnabled =
      hasAdminRole(auth) && moveGroupEnhancedEnabledFlag;

    const enableCloneHierarchyFlag = get(
      workspace,
      "config.featureFlags.enableCloneHierarchy",
      false
    );
    const cloneHierarchyEnabled =
      hasAdminRole(auth) && enableCloneHierarchyFlag;

    const defaultActions = [
      {
        type: TEAM_ACTION_MENU_TYPE.CHANGE_TEAM_DETAILS,
        isHidden: !team.type || showChangesEnabled,
        label: `Change details`,
        onClick: onChangeDetailsClick,
        modal: onChangeDetailsClick
          ? undefined
          : {
              Component: TeamDescriptionEditModal,
              name: "TeamDescriptionEditModal",
              props: { group: team },
            },
      },
      {
        type: TEAM_ACTION_MENU_TYPE.ADD_TEAM,
        isHidden: !canCreateChild(groupTypes, team) || showChangesEnabled,
        label: `Add ${config.isDemand ? "team" : "group"}`,
        modal: {
          Component: AddNewTeamModal,
          name: "AddNewTeamModal",
          props: { group: team, runGroupCacheUpdateOnAddition },
        },
      },
      {
        type: TEAM_ACTION_MENU_TYPE.DOWNLOAD,
        globalAction: GLOBAL_ACTION_KEYS.DOWNLOAD,
      },
      {
        type: TEAM_ACTION_MENU_TYPE.MOVE_TEAM,
        isHidden: !moveGroupEnabled || showChangesEnabled,
        label: `Move ${config.isDemand ? "team" : "group"}`,
        modal: {
          Component: MoveGroupModal,
          name: "MoveGroupModal",
          props: { group: team, groupTypes },
        },
      },
      {
        // isHidden: !canMoveGroupEnhanced(auth, workspace),
        type: TEAM_ACTION_MENU_TYPE.ADVANCED_MOVE,
        isHidden: !moveGroupEnhancedEnabled || showChangesEnabled,
        label: "Advanced Move",
        modal: {
          Component: MoveGroupEnhancedModal,
          name: "MoveGroupEnhancedModal",
          props: { rootGroup: team, groupTypes },
        },
      },
      {
        type: TEAM_ACTION_MENU_TYPE.REMOVE_TEAM,
        isHidden: !canHide || showChangesEnabled,
        label: "Remove team",
        modal: {
          Component: HideTeamModal,
          name: "HideTeamModal",
          props: { group: team },
        },
      },
      {
        type: TEAM_ACTION_MENU_TYPE.CLONE_HIERARCHY,
        isHidden: !cloneHierarchyEnabled || showChangesEnabled,
        label: "Clone hierarchy",
        modal: {
          Component: CloneHierarchyModal,
          name: "CloneHierarchyModal",
          props: { cloneToTeam: team, groupTypes },
        },
      },
      {
        type: TEAM_ACTION_MENU_TYPE.SELECT_COVER_IMAGE,
        isHidden: !canUpdateGroupAttributes || showChangesEnabled,
        label: "Select cover image",
        modal: {
          // eslint-disable-next-line react/prop-types
          Component: ({ onHideModal, isVisible }) => (
            <ImagePicker
              attributes={get(team, "attributes")}
              onUpdate={handleUpdateTeamDetails}
              isVisible={isVisible}
              onHideModal={onHideModal}
            />
          ),
          name: "UpdateBackgroundModal",
        },
      },
    ];

    const filteredActions = filter(defaultActions, (action) => {
      return includes(actionMenuTypes, action.type);
    });

    return filteredActions;
  }, [
    groupTypes,
    team,
    workspace,
    auth,
    canHide,
    canUpdateGroupAttributes,
    handleUpdateTeamDetails,
    actionMenuTypes,
    runGroupCacheUpdateOnAddition,
    showChangesEnabled,
    onChangeDetailsClick,
  ]);

  return actions;
};

export default useTeamActions;
